/**
 * sse.js - Server-Sent Events integration
 *
 * Enables real-time updates via SSE.
 * Content from SSE messages is swapped into target elements.
 */

import { resolve } from './cascade.js';
import { swap } from './request.js';

// Track initialized elements and their EventSource connections
const INIT_KEY = '_htmlstarSSE';
const connections = new Map();
let cleanupObserver;

/**
 * Ensure a global cleanup observer is watching for detached nodes
 */
function ensureCleanupObserver() {
  if (cleanupObserver) return;
  if (typeof document === 'undefined' || typeof MutationObserver === 'undefined') return;

  cleanupObserver = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      for (const node of mutation.removedNodes) {
        cleanupNode(node);
      }
    }
  });

  cleanupObserver.observe(document.documentElement || document.body, {
    childList: true,
    subtree: true
  });
}

/**
 * Close SSE connections and polling intervals for a removed node and descendants
 *
 * @param {Node} node
 */
function cleanupNode(node) {
  if (!node || node.nodeType !== 1) return;

  const el = node;
  if (connections.has(el)) {
    closeSSE(el);
  }
  if (el._htmlstarPoll) {
    stopPolling(el);
  }

  if (el.querySelectorAll) {
    el.querySelectorAll('[data-sse], [data-poll]').forEach((child) => {
      if (connections.has(child)) {
        closeSSE(child);
      }
      if (child._htmlstarPoll) {
        stopPolling(child);
      }
    });
  }
}

/**
 * Initialize SSE connection for an element
 *
 * @param {HTMLElement} el - Element with data-sse attribute
 */
function initSSEConnection(el) {
  const url = el.dataset.sse;
  if (!url) return;

  const source = new EventSource(url);

  // Handle incoming messages
  source.onmessage = (event) => {
    const swapStrategy = resolve(el, 'swap') || 'prepend';
    swap(el, event.data, swapStrategy);
  };

  // Handle named events
  const eventTypes = el.dataset.sseEvents;
  if (eventTypes) {
    const types = eventTypes.split(',').map(t => t.trim());
    for (const type of types) {
      source.addEventListener(type, (event) => {
        const swapStrategy = resolve(el, 'swap') || 'prepend';
        swap(el, event.data, swapStrategy);
      });
    }
  }

  // Handle errors
  source.onerror = (err) => {
    console.error('htmlstar SSE error:', err);
    el.dataset.sseError = '';
  };

  // Handle open
  source.onopen = () => {
    delete el.dataset.sseError;
    el.dataset.sseConnected = '';
  };

  // Store connection reference
  connections.set(el, source);
  el[INIT_KEY] = source;
}

/**
 * Close SSE connection for an element
 *
 * @param {HTMLElement} el - Element with SSE connection
 */
export function closeSSE(el) {
  const source = connections.get(el);
  if (source) {
    source.close();
    connections.delete(el);
    delete el[INIT_KEY];
    delete el.dataset.sseConnected;
  }
}

/**
 * Close all SSE connections
 */
export function closeAllSSE() {
  for (const [el, source] of connections) {
    source.close();
    delete el[INIT_KEY];
    delete el.dataset.sseConnected;
  }
  connections.clear();
}

/**
 * Initialize SSE for elements with data-sse attribute
 *
 * @param {HTMLElement|Document} root - Root element to search within
 */
export function initSSE(root) {
  ensureCleanupObserver();
  const elements = root.querySelectorAll('[data-sse]');

  for (const el of elements) {
    if (el[INIT_KEY]) continue;
    initSSEConnection(el);
  }
}

/**
 * Initialize polling for elements with data-poll attribute
 *
 * @param {HTMLElement|Document} root - Root element to search within
 * @param {Function} onInit - Callback for reinitializing new content
 */
export function initPolling(root, onInit) {
  ensureCleanupObserver();
  const elements = root.querySelectorAll('[data-poll]');

  for (const el of elements) {
    if (el._htmlstarPoll) continue;

    const interval = parseInterval(el.dataset.poll);
    if (!interval) continue;

    const url = el.dataset.href || el.dataset.poll;
    if (!url || url === el.dataset.poll) {
      // data-poll is just the interval, need a URL
      const href = el.dataset.href;
      if (!href) continue;
    }

    const poll = async () => {
      try {
        const targetUrl = el.dataset.href || url;
        const res = await fetch(targetUrl, {
          headers: { 'X-Requested-With': 'htmlstar' }
        });

        if (!res.ok) throw new Error(res.statusText);

        const html = await res.text();
        const swapStrategy = resolve(el, 'swap') || 'inner';
        await swap(el, html, swapStrategy);

        if (onInit) onInit(el);
      } catch (err) {
        console.error('htmlstar polling error:', err);
        el.dataset.pollError = '';
      }
    };

    el._htmlstarPoll = setInterval(poll, interval);
  }
}

/**
 * Parse interval string to milliseconds
 * Supports: "5s", "500ms", "1m", or plain numbers (ms)
 *
 * @param {string} str - Interval string
 * @returns {number|null}
 */
function parseInterval(str) {
  if (!str) return null;

  const match = str.match(/^(\d+)(ms|s|m)?$/);
  if (!match) return null;

  const value = parseInt(match[1], 10);
  const unit = match[2] || 'ms';

  switch (unit) {
    case 's': return value * 1000;
    case 'm': return value * 60 * 1000;
    default: return value;
  }
}

/**
 * Stop polling for an element
 *
 * @param {HTMLElement} el - Element with polling
 */
export function stopPolling(el) {
  if (el._htmlstarPoll) {
    clearInterval(el._htmlstarPoll);
    delete el._htmlstarPoll;
  }
}
